/**
 * Date utility functions for form validation and date handling
 */

/**
 * Get today's date in YYYY-MM-DD format for HTML date inputs
 */
export const getTodayString = (): string => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Check if a date string represents a past date
 * @param dateString - Date in YYYY-MM-DD format
 * @returns true if the date is in the past, false otherwise
 */
export const isPastDate = (dateString: string): boolean => {
  if (!dateString) return false;
  return dateString < getTodayString();
};

/**
 * Validate date range (end date must be >= start date)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns true if date range is valid, false otherwise
 */
export const isValidDateRange = (
  startDate: string,
  endDate: string,
): boolean => {
  if (!startDate || !endDate) return true;
  return new Date(startDate) <= new Date(endDate);
};

/**
 * Get validation error message for date issues
 * @param startDate - Start date string
 * @param endDate - End date string
 * @param isEditMode - Whether in edit mode (deprecated - now blocks past dates in all modes)
 * @returns Error message or null if valid
 */
export const getDateValidationError = (
  startDate: string,
  endDate: string,
  isEditMode: boolean = false,
): string | null => {
  // Check if start date is in the past (now blocks in all modes)
  if (isPastDate(startDate)) {
    return "Start date cannot be in the past";
  }

  // Check if end date is before start date
  if (!isValidDateRange(startDate, endDate)) {
    return "End date must be after or equal to start date";
  }

  return null;
};
